import * as Sentry from '@sentry/react';
// TYPES
import {
	UserJson,
} from '@@types/User';
import {
	EnumEnvironments,
} from '@enums/environments.enum';

interface argsProps {
	messageContext?: string;
	status?: string | Error;
}

export interface LoggerProps {
	init: () => void;
	initSentry: () => void;
	setUser: (user: UserJson) => void;
	canLog: () => boolean;
	log: (msg: string, args: argsProps) => void;
}

export class Logger implements LoggerProps {
	private static instance: Logger;
	env: EnumEnvironments;
	SENTRY_DSN: string;
	initialized?: boolean;
	sentryClient?: any;

	private constructor({
		env,
		SENTRY_DSN,
	}: {
		env: EnumEnvironments;
		SENTRY_DSN?: string;
	}) {
		// Var to check if env is prod
		this.env = env || EnumEnvironments.DEVELOPMENT;
		this.SENTRY_DSN = SENTRY_DSN || '';
		this.initialized = false;
		this.sentryClient = null;

		this.initSentry = this.initSentry.bind(this);
	}

	public static getInstance({
		env,
		SENTRY_DSN,
	}: {
		env: EnumEnvironments;
		SENTRY_DSN: string;
	}): Logger {
		if (!Logger.instance) {
			Logger.instance = new Logger({
				env,
				SENTRY_DSN,
			});
			Logger.instance.init();
		}
		return Logger.instance;
	}

	init() {
		if (this.initialized) return;
		this.initSentry();
		this.initialized = true;
	}

	private sanitizeServiceName(service: string): string {
		if (!service) return 'unknown-service';
		// Replace any non-alphanumeric characters (except allowed ones) with underscores
		return service.toLowerCase().replace(/[^a-z0-9_\-.,/:]/g, '_');
	}

	initSentry() {
		/* istanbul ignore next */
		if (this.sentryClient !== null) return;
		/* istanbul ignore next */
		this.sentryClient = Sentry.init({
			debug: false,
			dsn: this.SENTRY_DSN,
			environment: this.env,
			integrations: [
				Sentry.browserTracingIntegration(),
				Sentry.replayIntegration(),
				Sentry.browserProfilingIntegration(),
			],
			// Set profilesSampleRate to 1.0 to profile every transaction.
			// Since profilesSampleRate is relative to tracesSampleRate,
			// the final profiling rate can be computed as tracesSampleRate * profilesSampleRate
			// For example, a tracesSampleRate of 0.5 and profilesSampleRate of 0.5 would
			// results in 25% of transactions being profiled (0.5*0.5=0.25)
			profilesSampleRate: 1.0,
			// Alternatively, use `process.env.npm_package_version` for a dynamic release version
			// if your build tool supports it.
			release: process.env.npm_package_version || '1.0.0',
			replaysOnErrorSampleRate: 1.0,
			// Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
			// tracePropagationTargets: ["localhost", /^https:\/\/yourserver\.io\/api/],
			// Capture Replay for 10% of all sessions,
			// plus for 100% of sessions with an error
			replaysSessionSampleRate: 0.1,
			// Set tracesSampleRate to 1.0 to capture 100%
			// of transactions for tracing.
			// We recommend adjusting this value in production
			tracesSampleRate: 1.0,
		});
	}

	/* istanbul ignore next */
	setUser(user: UserJson) {
		/* istanbul ignore next */
		if (this.sentryClient !== null && this.SENTRY_DSN) {
			this.sentryClient.setUser({
				email: user.email || undefined,
				id: user.id || 0,
				username: user.username || undefined,
			});
		}
	}

	canLog() {
		return ![
			EnumEnvironments.DEVELOPMENT,
			EnumEnvironments.JEST,
		].includes(this.env as EnumEnvironments);
	}

	log(msg: string, args: argsProps = {}) {
		if (!this.initialized || !msg) return;
		const argsOptions = {
			messageContext: args?.messageContext || {},
			msg: msg,
			status: args?.status || 'info',
			...args,
		};
		/* istanbul ignore next */
		if (!this.canLog()) {

			console.log('LOGGER - MSG :', argsOptions);
		}
	}
}
